import { Badge } from 'components/shared/Badges/Badge';
import { BestQuote } from './BestQuote';
import styled, { keyframes } from 'styled-components';
import { OkoAssistant } from '../OkoAssistant/OkoAssistant';
import { AnimatedRays } from './Starburst/AnimatedRays';
import { Sparkles } from './Starburst/Sparkles';
import { ScreenTitle } from 'components/shared/ScreenTitle/ScreenTitle';

const OkoLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="83" height="26" viewBox="0 0 83 26" fill="none">
    <path d="M68.6338 7.16781C65.1344 7.16781 62.2947 9.6279 62.2947 12.6359C62.2947 15.6439 65.1344 18.104 68.6338 18.104C72.1331 18.104 74.9728 15.6439 74.9728 12.6359C74.9728 9.6279 72.1219 7.16781 68.6338 7.16781ZM68.6338 25.2718C60.696 25.2718 54.2675 19.6136 54.2675 12.6359C54.2675 5.65821 60.7072 0 68.6338 0C76.5715 0 83 5.65821 83 12.6359C83 19.6136 76.5715 25.2718 68.6338 25.2718ZM14.3774 7.16781C10.8781 7.16781 8.03839 9.6279 8.03839 12.6359C8.03839 15.6439 10.8781 18.104 14.3774 18.104C17.8768 18.104 20.7165 15.6439 20.7165 12.6359C20.7165 9.6279 17.8656 7.16781 14.3774 7.16781ZM14.3662 25.2718C6.43965 25.2718 0 19.6136 0 12.6359C0 5.65821 6.43965 0 14.3662 0C22.2928 0 28.7325 5.65821 28.7325 12.6359C28.7437 19.6136 22.304 25.2718 14.3662 25.2718ZM46.8552 12.6359L58.147 24.9252H48.1074L38.5709 14.5928V24.914H30.3536V0.346649H38.5709V10.6567L48.0626 0.335466H58.147L46.7993 12.6359H46.8552Z" fill="#BC165D" />
  </svg>
);

export const DiscussionAnalyzed = ({
  display,
  isLoading = false,
  level = 1,
  quote = "",
  name = "",
  badgeType = "",
  badgeTitle = "",
  phrase = "",
  showText = false,
  setShowText = () => { },
  isPlayingAudio = false
}) => {
  const isStealth = badgeTitle?.includes("Stealth");
  return (
    <>
      <ScreenTitle name={display ? "Collaboration Score screen" : ""} />
      <Overlay $display={display}>
      <Content $display={display}>
        {!isLoading && <BadgeTextContainer $isLoading={isLoading}>
          <OkoLogo />

          {isStealth ? null :
            <LevelTextContainer>
              <LevelLine />
              <LevelText>{badgeType} Level {level}</LevelText>
              <LevelLine />
            </LevelTextContainer>
          }
          <BadgeTitle>{badgeTitle}</BadgeTitle>
        </BadgeTextContainer>}

        <BadgeContainer>
          <Badge isLoading={isLoading || isStealth} level={level} />
          <AnimatedRays />
          <Sparkles count={50} />
        </BadgeContainer>

        <OkoAssistant
          phrase={phrase}
          showText={showText}
          setShowText={() => setShowText(false)}
          isPlayingAudio={isPlayingAudio}
          positionLeft={40}
          positionBottom={150}
          arrowDirection="bottom"
          speechTextMinWidth={400}
          maxLength={1000}
        />
        {!!quote && (
          <Footer>
            <BestQuote isLoading={isLoading} quote={quote} name={name} />
          </Footer>
        )}
      </Content>
      <LeftCurtain $display={display} $side="left" />
      <RightCurtain $display={display} $side="right" />
      </Overlay>
    </>
  );
};

export const Footer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem 0;
  z-index: 10020;
`

const curtainLeftOpen = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const curtainRightOpen = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const curtainLeftClose = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const curtainRightClose = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  display: ${props => props.$display ? 'flex' : 'none'};
  overflow: hidden;
`;

const CurtainPanel = styled.div`
  position: absolute;
  top: 0;
  width: 50%;
  height: 100%;
  background: #D91A6C;
  box-shadow: ${props => {
    const shadow = props.$side === 'left'
      ? 'inset -20px 0 40px rgba(0, 0, 0, 0.3)'
      : 'inset 20px 0 40px rgba(0, 0, 0, 0.3)';
    const noShadow = 'inset 0 0 0 rgba(0, 0, 0, 0)';
    return props.$display ? noShadow : shadow;
  }};
  transition: box-shadow 0.3s ease-in-out 0.5s;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    ${props => props.$side === 'left' ? 'left: 0' : 'right: 0'};
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 40px,
      rgba(0, 0, 0, 0.1) 40px,
      rgba(0, 0, 0, 0.1) 80px
    );
    opacity: ${props => props.$display ? 0 : 1};
    transition: opacity 0.3s ease-in-out 0.5s;
  }
`;

const LeftCurtain = styled(CurtainPanel)`
  left: 0;
  animation: ${props => props.$display ? curtainLeftClose : curtainLeftOpen} 0.5s cubic-bezier(0.65, 0, 0.35, 1);
  animation-fill-mode: forwards;
`;

const RightCurtain = styled(CurtainPanel)`
  right: 0;
  animation: ${props => props.$display ? curtainRightClose : curtainRightOpen} 0.5s cubic-bezier(0.65, 0, 0.35, 1);
  animation-fill-mode: forwards;
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: 2rem;
  color: white;
  text-align: center;
  z-index: 10010;
  opacity: ${props => props.$display ? 1 : 0};
  transition: opacity 0.3s ease-in-out ${props => props.$display ? '0.6s' : '0s'};
`;

const BadgeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const fadeInDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BadgeTextContainer = styled.div`
  position: absolute;
  top: 32px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 10010;
  opacity: 0;
  animation: ${fadeInDown}
    ${props => props.$isLoading ? '0.5s' : '0.4s'}
    ${props => props.$isLoading ? 'cubic-bezier(0.65, 0, 0.35, 1)' : 'ease-out'}
    forwards;
  animation-delay: ${props => props.$isLoading ? '0s' : '0.2s'};
`;

const LevelTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LevelLine = styled.div`
  opacity: 0.8;
  background: var(--Pink-200, #FFCCD9);
  width: 101px;
  height: 1px;
`;

const LevelText = styled.p`
  color: #FFF;
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
`;

const BadgeTitle = styled.h1`
  color: #FFF;
  text-align: center;
  font-size: 60px;
  font-style: normal;
  font-weight: 600;
  line-height: 72px;
  letter-spacing: 0.25px;
  text-transform: uppercase;
  margin: 0;
`;
